import {
	IChartPoint,
	IResultTrends,
	ITrendsForXGridTooltip,
	IXGridChartTooltip,
} from '../../models';
import { findCurrentRound } from '../sortTrends';
import { roundNumber } from '../../../../../../helpers';
import { MAX_ONE_LINE_STRING_SYMBOLS, POINT_RADIUS_PX } from './chartConstants';

/**
 * function splits up a string to two lines with \n
 * @param str - a string to split
 * @param spaceIndex - an index where to split the string
 */
const replaceSpaceWithLineBreak = (str: string, spaceIndex: number): string => {
	return `${str.substring(0, spaceIndex)}\n${str.substring(spaceIndex + 1)}`;
};

/**
 * function defines where to split up a multiword string to two lines with \n
 * it takes a middle index (represent a symbol in the middle of the string)
 * and searches for the closest space to it to break
 * @param label - a string to split
 */
const formatMultiwordLabel = (label: string): string => {
	const midIndex = Math.round(label.length / 2);

	if (label[midIndex] === ' ') {
		return replaceSpaceWithLineBreak(label, midIndex);
	}

	const leftSpaceIndex = label.lastIndexOf(' ', midIndex);
	const rightSpaceIndex = label.indexOf(' ', midIndex);

	if (leftSpaceIndex === -1 && rightSpaceIndex === -1) return label;
	if (leftSpaceIndex === -1) {
		return replaceSpaceWithLineBreak(label, rightSpaceIndex);
	}
	if (rightSpaceIndex === -1) {
		return replaceSpaceWithLineBreak(label, leftSpaceIndex);
	}

	const indexToReplace =
		rightSpaceIndex - midIndex > midIndex - leftSpaceIndex
			? leftSpaceIndex
			: rightSpaceIndex;

	return replaceSpaceWithLineBreak(label, indexToReplace);
};

/**
 * Function splits up a string to two lines with \n
 * and does this as close as possible to the middle of the string
 * to make two lines be with approximately the same width.
 * @param label - a string to split
 */
export const formatLabel = (label: string): string => {
	const wordCount = label.split(' ').length;

	if (label.length <= MAX_ONE_LINE_STRING_SYMBOLS) return label;

	switch (wordCount) {
		case 1:
			return label;
		case 2:
			return label.replace(' ', '\n');
		default:
			return formatMultiwordLabel(label);
	}
};

/**
 * Return an object that contains necessary information for the XGrid chart
 * to do this it takes an array of service trends and get data from it.
 * For the dashboard and service pages trendItems contains info for several rounds
 * so roundId is needed to find data for the current round.
 * For the persona page roundId isn't in need
 * because the trendItems array contain info only for persona, not for a round
 * @param trendItems - array of service trends
 * @param roundId - id of a survey round
 * @param isBlank - whether X Grid is blank or not -> filters
 * @param size - size of the point
 */
export const getChartData = (
	trendItems?: IResultTrends[],
	roundId?: string,
	isBlank?: boolean,
	size?: number[],
): IChartPoint[] => {
	if (!trendItems) return [];

	const influenceValues = trendItems.map((trend) => {
		return findCurrentRound(trend, roundId)?.influence || trend.influence || 0;
	});
	const max = Math.max(...influenceValues);
	const min = Math.min(...influenceValues);

	if (isBlank) {
		// first point anchors so the second one can be put in the middle
		return [
			{
				x: 23,
				y: 50,
				r: 0,
				label: '',
				priority: '',
			},
			{
				x: 45,
				y: 55,
				r: 0,
				label: 'No X-Grid Available',
				priority: '',
			},
		];
	}

	return trendItems.map((trend, index) => {
		const currentRound = findCurrentRound(trend, roundId) || trend;
		if (!currentRound) return {} as IChartPoint;
		const { satisfaction, influence, priority } = currentRound;
		return {
			x: satisfaction || 0,
			y: ((influence - min) / (max - min)) * 100,
			r: size ? size[index] : 12,
			label: formatLabel(trend.serviceName) || '',
			priority: priority?.toString() || '',
		};
	});
};

/**
 * Return data for the XGrid tooltip
 * For the dashboard and service pages trendItems contains info for several rounds
 * so roundId is needed to find data for the current round.
 * For the persona page roundId isn't in need
 * because the trendItems array contains info only for persona, not for a round
 * @param trendItems - array of service trends
 * @param roundId - id of a survey round
 */
export const getTooltipData = (
	trendItems?: ITrendsForXGridTooltip[],
	roundId?: string,
): IXGridChartTooltip[] => {
	if (!trendItems) return [];

	return trendItems.map((trend) => {
		const currentRound = findCurrentRound(trend, roundId) || trend;

		return {
			name: trend.serviceName,
			examples: currentRound?.examples || [],
			influence: roundNumber(currentRound?.influence || 0, 1),
			satisfaction: roundNumber(currentRound?.satisfaction || 0, 1),
			avgMark: currentRound?.avgMark
				? roundNumber(currentRound?.avgMark, 1)
				: undefined,
		};
	});
};

/**
 * Return array of service trends that don't contain influence info
 * so they can't be used to build XGrid chart.
 * For the dashboard and service pages trendItems contains info for several rounds
 * so roundId is needed to find data for the current round.
 * For the persona page roundId isn't in need
 * because the trendItems array contains info only for persona, not for a round
 * @param trendItems - array of service trends
 * @param roundId - id of a survey round
 */
export const getItemsWithoutInfluence = (
	trendItems?: IResultTrends[],
	roundId?: string,
): string[] => {
	if (!trendItems) return [];

	return trendItems.reduce((resItems, trend) => {
		const currentRound = findCurrentRound(trend, roundId) || trend;
		if (!currentRound || !currentRound.influence)
			return [...resItems, trend.serviceName];
		return resItems;
	}, [] as string[]);
};
